import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import axios from "axios";
import { scrollToId } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const lines = ["Preserve Your", "Family’s Legacy", "Across Generations."];

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
    const [spotsLeft, setSpotsLeft] = useState(null);

    useEffect(() => {
        axios
            .get(`${API}/auth/signup-stats`)
            .then(({ data }) => setSpotsLeft(data.free_left))
            .catch(() => {});
    }, []);

    return (
        <section ref={ref} data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden">
            <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-6xl px-6 pt-32 pb-24 text-center lg:px-10">
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                    className="mb-8 font-mono text-xs font-semibold uppercase tracking-[0.35em] text-[#C98A94]"
                >
                    Est. 2026 · Private by design
                </motion.p>

                <h1 data-testid="hero-heading" className="font-display text-4xl leading-[1.08] tracking-tight sm:text-6xl lg:text-7xl">
                    {lines.map((line, i) => (
                        <span key={line} className="block overflow-hidden pb-1">
                            <motion.span
                                className={`block ${i === 1 ? "italic" : ""}`}
                                initial={{ y: "110%" }}
                                animate={{ y: 0 }}
                                transition={{ duration: 1.1, ease: [0.76, 0, 0.24, 1], delay: 0.25 + i * 0.14 }}
                            >
                                {line}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    data-testid="hero-subheadline"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.85 }}
                    className="mx-auto mt-8 max-w-2xl text-base font-medium text-[#2C3347] sm:text-lg"
                >
                    A private, secure space for your family’s photos, videos, stories, and voice memories.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.05 }}
                    className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
                >
                    <Link
                        to="/auth?mode=register"
                        data-testid="cta-create-account"
                        className="btn-gradient rounded-full px-8 py-4 text-sm font-semibold tracking-wide text-white shadow-xl shadow-navy/25 transition-transform duration-300 hover:-translate-y-1"
                    >
                        Create your free account
                    </Link>
                    <button
                        data-testid="cta-founding-supporter"
                        onClick={() => scrollToId("support")}
                        className="rounded-full border-2 border-navy bg-white/60 px-8 py-4 text-sm font-semibold tracking-wide text-navy backdrop-blur-sm transition-transform duration-300 hover:-translate-y-1"
                    >
                        Become a Founding Supporter
                    </button>
                </motion.div>

                {spotsLeft !== null && spotsLeft > 0 && (
                    <motion.p
                        data-testid="founding-spots"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.9, delay: 1.25 }}
                        className="mt-7 inline-flex items-center gap-2 rounded-full border-2 border-peach bg-ivory px-5 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-navy"
                    >
                        <span className="h-2 w-2 rounded-full bg-gradient-to-br from-lavender to-sunset" />
                        Founding 100 · {spotsLeft} of 100 free-forever spots left
                    </motion.p>
                )}
            </motion.div>

            <motion.button
                data-testid="hero-scroll-cue"
                aria-label="Scroll down"
                onClick={() => scrollToId("chapters")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-indigo/60"
            >
                <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }} className="block">
                    <ArrowDown size={20} />
                </motion.span>
            </motion.button>
        </section>
    );
}
